import React, { useEffect } from 'react';
import styled from 'styled-components';

// Modal Overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease-in-out;
`;

// Modal Container
const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 0;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  position: relative;
  transform: ${({ isOpen }) => (isOpen ? 'scale(1)' : 'scale(0.9)')};
  transition: all 0.3s ease-in-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 95%;
    max-width: 500px;
    margin: 20px;
  }
`;

// Close Button
const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 20px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  z-index: 1001;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #333;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

// Modal Header
const ModalHeader = styled.div`
  background: linear-gradient(135deg, #4285f4, #34a853);
  padding: 20px 30px;
  color: white;
  position: relative;
  overflow: hidden;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const LocationIcon = styled.span`
  font-size: 24px;
`;

// Modal Content
const ModalContent = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 500px;

  @media (max-width: 768px) {
    height: 400px;
  }
`;

// Google Maps Iframe Container
const GoogleMapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 16px 16px;
`;

const GoogleMapIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 0 0 16px 16px;
`;

// Location Info Overlay
const LocationInfo = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    left: 15px;
    bottom: 15px;
    padding: 12px 16px;
  }
`;

const LocationText = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #1a73e8;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const LocationSubtext = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const LocationModal = ({ isOpen, onClose }) => {
  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Google Maps embed URL for United States with perfect zoom level
  const googleMapsEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25308126.05348663!2d-119.66793087500001!3d39.50404070312501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54eab584e432360b%3A0x1c3bb99243deb742!2sUnited%20States!5e0!3m2!1sen!2sus!4v1691234567890!5m2!1sen!2sus";

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer isOpen={isOpen}>
        <CloseButton onClick={onClose}>
          ×
        </CloseButton>

        <ModalHeader>
          <ModalTitle>
            <LocationIcon>📍</LocationIcon>
            My Location
          </ModalTitle>
        </ModalHeader>

        <ModalContent>
          <GoogleMapContainer>
            <GoogleMapIframe
              src={googleMapsEmbedUrl}
              title="Google Maps - United States Location"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <LocationInfo>
              <LocationText>🇺🇸 United States</LocationText>
              <LocationSubtext>Currently based in the USA</LocationSubtext>
            </LocationInfo>
          </GoogleMapContainer>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default LocationModal;
